import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Layout, Button, Typography } from 'antd';
import { LogoutOutlined, ThunderboltOutlined } from '@ant-design/icons';
import 'antd/dist/reset.css';

import Home from './pages/Home';
import HowItWorks from './pages/HowItWorks';
import Features from './pages/Features';
import FAQ from './pages/FAQ';
import About from './pages/About';
import Navigation from './components/Navigation';

const { Header, Content, Footer } = Layout;
const { Title, Text } = Typography;

// ScrollToTop component to reset scroll position on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      setIsLoggedIn(true);
    }
  }, []);

  const handleLogin = () => setIsLoggedIn(true);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setIsLoggedIn(false);
  };

  return (
    <Router>
      <ScrollToTop />
      <Layout className="min-h-screen flex flex-col bg-slate-950 text-slate-100 relative overflow-x-hidden font-['Outfit']">
        {/* Background Elements */}
        <div className="fixed inset-0 z-0 bg-grid-pattern opacity-40 pointer-events-none"></div>
        <div className="fixed top-[-10%] left-[-5%] w-[40%] h-[40%] bg-purple-600/10 blur-[100px] rounded-full z-0 pointer-events-none"></div>
        <div className="fixed bottom-[-10%] right-[-5%] w-[40%] h-[40%] bg-indigo-600/10 blur-[100px] rounded-full z-0 pointer-events-none"></div>
        
        <Header className="sticky top-0 z-50 flex items-center justify-between glass-panel-dark px-6 md:px-12 py-5 h-20 border-b border-white/5 bg-transparent shadow-none">
          <div className="flex items-center gap-4 animate-fade-in-up">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30 animate-pulse-glow">
              <ThunderboltOutlined className="text-white text-xl md:text-2xl" />
            </div>
            <div>
              <Title level={3} className="!mb-0 !text-white !font-bold tracking-tight text-glow flex items-center gap-1 md:gap-2">
                COMPRESS<span className="text-purple-400 font-light">AI</span>
              </Title>
              <Text className="text-purple-200/40 text-[10px] md:text-xs font-medium uppercase tracking-widest hidden sm:block">Next-Gen Image Optimization</Text>
            </div>
            <Navigation />
          </div>
          
          {isLoggedIn && (
            <Button
              type="primary"
              danger
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              className="hover:scale-105 transition-all duration-300 shadow-lg shadow-red-500/10 border-0 bg-slate-800 hover:bg-red-500/10 text-slate-300 hover:text-red-400 h-10 px-4 md:px-6 rounded-xl flex items-center justify-center font-bold"
            >
              Sign Out
            </Button>
          )}
        </Header>

        <Content className="relative z-10 flex-1 flex flex-col overflow-y-auto">
          <Routes>
            <Route path="/" element={<Home isLoggedIn={isLoggedIn} onLogin={handleLogin} />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/features" element={<Features />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/about" element={<About />} />
          </Routes>
        </Content>

        <Footer className="relative z-20 glass-panel-dark border-t border-white/5 py-12 bg-transparent shadow-none mt-auto">
          <div className="max-w-6xl mx-auto px-6">
            <Navigation mode="footer" />
            
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center gap-2 text-slate-400">
                <ThunderboltOutlined className="text-purple-500" />
                <span className="font-semibold text-slate-300 tracking-wider text-glow uppercase text-xs">COMPRESSAI ENGINE v1.0</span>
              </div>
              <Text className="text-slate-500 text-xs tracking-widest uppercase text-center">
                Built with extreme precision for high-performance workflows
              </Text>
              <div className="mt-4 flex items-center justify-center gap-2">
                <span className="h-px w-8 bg-gradient-to-r from-transparent to-slate-700"></span>
                <Text className="text-slate-400 text-[10px] sm:text-xs font-medium tracking-wide">
                  Powered by <span className="text-purple-400 font-bold hover:text-purple-300 transition-colors duration-300 cursor-pointer">James La</span> - <a href="https://afi.io/" target="_blank" rel="noopener noreferrer" className="text-purple-400 font-bold hover:text-purple-300 transition-colors duration-300">Afi Labs Inc.</a>
                </Text>
                <span className="h-px w-8 bg-gradient-to-l from-transparent to-slate-700"></span>
              </div>
            </div>
          </div>
        </Footer>
      </Layout>
    </Router>
  );
}

export default App;
